import BaseGame from "./BaseGame.js";
import StorageService from "../services/StorageService.js";
import StringUtils from "../utils/StringUtils.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import { loadCountryDetails } from "../utils/CountryDetails.js";

const escapeHtml = (s) => String(s)
	.replace(/&/g, "&amp;")
	.replace(/</g, "&lt;")
	.replace(/>/g, "&gt;")
	.replace(/"/g, "&quot;");

/** Seuil des noms tapés, le même que dans les autres modes à saisie. */
const THRESHOLD = 0.78;

/**
 * Border Nerd (53 du TODO, Lab) : citer tous les pays frontaliers d'un pays.
 * Trois vies ; un pays tapé qui n'est pas un voisin en coûte une, comme
 * « Je passe », qui dévoile les voisins manquants. Un pays est trouvé quand
 * tous ses voisins le sont. Seuls les pays d'au moins deux voisins (frontières
 * terrestres de Wikidata, outre-mer compris : la France touche le Brésil par la
 * Guyane) sont posés.
 *
 * Les voisins déjà trouvés de la question en cours sont sauvegardés
 * (`{gameId}.partial`) : un rechargement ne les fait pas perdre.
 */
export default class BorderNerd extends BaseGame {
	constructor() {
		super("bordernerd", ".border-nerd", {
			labMode: "bordernerd",
			gameNameKey: "lab.borderNerd",
			modeLabelKey: "home.classic",
			mode: "classic",
			shareEmoji: "🟫",
			slug: "border-nerd",
		});
		if (!this.page) return;
		this.question = this.page.querySelector(".border-question");
		this.chips = this.page.querySelector(".border-chips");
		this.answerInput = this.page.querySelector(".answer-input");
		this.validateButton = this.page.querySelector(".validate-input");
		this.skipButton = this.page.querySelector(".border-skip");
		this.feedback = this.page.querySelector(".border-feedback");
		this.byCode = new Map(geoNerdApp.countries.map(c => [c.code, c]));
		this.bindInput();

		loadCountryDetails(geoNerdApp.languageManager.currentLang)
			.then(details => {
				this.details = details;
				this.pool = geoNerdApp.countries.filter(c => this.neighboursOf(c.code).length >= 2);
				this.init();
			})
			.catch(() => {
				if (this.question) this.question.innerHTML = `<p class="lab-error">${geoNerdApp.languageManager.t("lab.dataError")}</p>`;
			});
	}

	/** Voisins jouables (pays de la liste, sans doublon). */
	neighboursOf(code) {
		return [...new Set(this.details?.[code]?.borders ?? [])].filter(c => this.byCode.has(c));
	}

	initCountries() {
		super.initCountries(this.pool);
	}

	newProposal() {
		super.newProposal();
		if (this.countriesLeft.length === 0) return;
		const partial = StorageService.load(`${this.gameId}.partial`);
		this.found = partial && partial.code === this.rightAnswer.code ? partial.found : [];
		this.canGuess = true;
		this.render();
		this.answerInput.value = "";
		this.answerInput.focus({ preventScroll: true });
	}

	render(revealAll = false) {
		const t = (k) => geoNerdApp.languageManager.t(k);
		const neighbours = this.neighboursOf(this.rightAnswer.code);
		this.question.innerHTML = `
			<div class="flag" style="background-image: url(${getFlagPath(this.rightAnswer.code)})"></div>
			<p class="border-country">${escapeHtml(this.rightAnswer.name)}</p>
			<p class="border-count">${t("lab.borderCount").replace("{found}", this.found.length).replace("{n}", neighbours.length)}</p>`;
		this.chips.innerHTML = neighbours.map(code => {
			const known = this.found.includes(code);
			if (!known && !revealAll) return `<span class="border-chip is-hidden" aria-hidden="true">?</span>`;
			return `<span class="border-chip${known ? " is-found" : " is-missed"}">${escapeHtml(this.byCode.get(code).name)}</span>`;
		}).join("");
		if (this.feedback) this.feedback.textContent = "";
	}

	saveProgressPartial() {
		StorageService.save(`${this.gameId}.partial`, { code: this.rightAnswer.code, found: this.found });
	}

	bindInput() {
		this.answerInput?.addEventListener("keydown", (e) => {
			if (e.key === "Enter") {
				e.preventDefault();
				this.validate(this.answerInput.value);
				this.answerInput.focus({ preventScroll: true });
			}
		});
		this.validateButton?.addEventListener("touchstart", (e) => { e.preventDefault(); this.validate(this.answerInput.value); }, { passive: false });
		this.validateButton?.addEventListener("click", () => this.validate(this.answerInput.value));
		this.skipButton?.addEventListener("click", () => this.skip());
	}

	validate(answer) {
		if (!this.canGuess || !answer || !answer.trim()) return;
		const t = (k) => geoNerdApp.languageManager.t(k);
		const { item, score, ambiguous } = StringUtils.bestMatch(answer, geoNerdApp.countries);
		this.answerInput.value = "";

		// Trop loin de tout pays, ou à égalité entre deux : on redemande, sans sanction.
		if (!item || score < THRESHOLD || ambiguous) {
			this.feedback.textContent = t("lab.borderUnknown");
			return;
		}
		if (this.found.includes(item.code)) {
			this.feedback.textContent = t("lab.borderAlready").replace("{name}", item.name);
			return;
		}

		const neighbours = this.neighboursOf(this.rightAnswer.code);
		if (neighbours.includes(item.code)) {
			SoundManager.correct();
			FeedbackEffects.confetti(this.answerInput);
			this.found.push(item.code);
			this.saveProgressPartial();
			this.render();
			if (this.found.length === neighbours.length) {
				this.canGuess = false;
				StorageService.remove(`${this.gameId}.partial`);
				this.markFound();
				this.advanceTurn();
			}
			return;
		}

		// Un vrai pays, mais pas un voisin : une vie, et la question continue.
		SoundManager.wrong();
		FeedbackEffects.shake(this.page.querySelector(".game-container"));
		this.feedback.textContent = t("lab.borderWrong").replace("{name}", item.name);
		this.recordError({ countryName: this.rightAnswer.name, correctCode: this.rightAnswer.code, guessed: item.name, missing: [] });
		this.penalizeLife();
		this.updateLife(false);
	}

	/** Dévoile les voisins manquants, coûte une vie, et passe au pays suivant. */
	skip() {
		if (!this.canGuess) return;
		this.canGuess = false;
		const missing = this.neighboursOf(this.rightAnswer.code).filter(c => !this.found.includes(c));
		this.render(true);
		StorageService.remove(`${this.gameId}.partial`);
		this.markWrong({
			countryName: this.rightAnswer.name,
			correctCode: this.rightAnswer.code,
			guessed: null,
			missing: missing.map(c => this.byCode.get(c).name),
		});
		this.advanceTurn();
	}

	renderErrorItem(error) {
		const t = (k) => geoNerdApp.languageManager.t(k);
		const detail = error.guessed
			? `<div class="error-answer-text wrong">${escapeHtml(error.guessed)}</div>`
			: `<div class="error-answer-text correct">${escapeHtml(error.missing.join(", "))}</div>
			   <div class="error-answer-text wrong">${t("lab.borderSkipped")}</div>`;
		return `
			<div class="error-item">
				<div class="error-country">${escapeHtml(error.countryName)}</div>
				<div class="error-flag correct"><img src="${getFlagPath(error.correctCode)}" alt="${escapeHtml(error.countryName)}"></div>
				<div class="error-details">${detail}</div>
			</div>`;
	}
}
